// ============================================================
// healProtagonist - Protagonist rules restored from the library.
//
// Until 2026-11-04 the scene library stripped every cast member's
// Protagonist rules on save (the strip was meant for files handed away),
// so a protagonist brought in by a library scene could die. Only a scene
// file written then (v1, `castMayHaveLostProtagonists`) is healed: a cast
// member with no rules who remembers the library character he came from
// (`sheet.origin`) takes that character's rules AS THEY WERE in the
// version he was copied from. A copy with rules of its own keeps them.
//
// Pure. Reading the library is the caller's (`assetSession.ts`); a
// character that could not be read simply heals nobody.
// ============================================================
import type { Character } from '../character/types';
import type { Origin } from '../assets/types';
import type { Combatant } from './types';

/** One library version, as a key: the same character at two versions holds two sets of rules. */
export const originKey = (origin: Origin): string => `${origin.assetId}@${origin.version}`;

/**
 * The copy with the origin's Protagonist rules when it has none, and the
 * SAME object otherwise: projections are memoised on identity, so an
 * unchanged sheet must stay the one it was.
 */
export function withProtagonistFrom(sheet: Character, origin: Character): Character {
  if (sheet.protagonist || !origin.protagonist) return sheet;
  return { ...sheet, protagonist: origin.protagonist };
}

/** The library versions to read: each one a rule-less cast member was copied from, once, in cast order. */
export function castToHeal(cast: readonly Combatant[]): Origin[] {
  const wanted = new Map<string, Origin>();
  for (const { sheet } of cast) {
    if (!sheet.protagonist && sheet.origin) wanted.set(originKey(sheet.origin), sheet.origin);
  }
  return [...wanted.values()];
}

/** The cast, each member healed from the version read for him (by `originKey`); the same array if none changed. */
export function healCast(cast: Combatant[], read: ReadonlyMap<string, Character>): Combatant[] {
  let changed = false;
  const next = cast.map((c) => {
    const origin = c.sheet.origin && read.get(originKey(c.sheet.origin));
    const sheet = origin ? withProtagonistFrom(c.sheet, origin) : c.sheet;
    if (sheet === c.sheet) return c;
    changed = true;
    return { ...c, sheet };
  });
  return changed ? next : cast;
}
